import { Divider, Skeleton, Typography } from '@fox/atoms';
import { formatCurrencyFromMinorUnits, FormFinancialLineItem } from '@fox/finance';
import { FlexColumnSmallSpaced, FlexRowSpacedRightAligned } from '@fox/layout';
import { useFormRuntimeContext } from '../../form-runtime-context';
import { QuestionStyle } from '../../form-runtime-context/contracts';
import { TaxRatesData } from './contracts';
import { calculateItemTax, calculateTaxTotal } from './helpers';

interface PaymentSectionSummaryProps {
	labelStyles: Record<string, QuestionStyle>;
	lineItems: FormFinancialLineItem[];
	total: number;
	summaryLabel: 'Due Now' | 'Due Later';
	taxRates?: TaxRatesData | null;
	defaultCurrency?: string;
}

const PaymentSectionSummary = ({ labelStyles, lineItems, total, summaryLabel, taxRates, defaultCurrency }: PaymentSectionSummaryProps) => {
	const { getAnswer } = useFormRuntimeContext();
	
	const totalTaxAmount = calculateTaxTotal(lineItems, taxRates?.rates);
	return (
		<FlexColumnSmallSpaced style={{ flex: 1, minWidth: 'min(20rem, 50%)' }}>
			<div>
				<Divider textAlign="center">
					<Typography sx={labelStyles}>{summaryLabel}</Typography>
				</Divider>
			</div>
			{lineItems.map((lineItem, i) => {
				const taxRateId = lineItem.invoice?.taxRate;
				const taxRate = (taxRates?.rates?.find(t => t.id === taxRateId) ?? taxRates?.rates?.find(t => t.default))?.rate;
				const taxAmountToSubtract = lineItem.invoice?.taxInclusive === false ? 0 : calculateItemTax(lineItem, taxRate);
				const amount = lineItem.amount - taxAmountToSubtract;
				const formatted = formatCurrencyFromMinorUnits(amount, defaultCurrency);

				// basic merge field replacement for {} magic strings
				const variableMatch = /{(.*?)}/;
				let comment = lineItem.comment;
				if (comment?.includes('{')) {
					for (let i = 0; i < 5; i++) {
						const match = variableMatch.exec(comment);
						if (!match) break;
						const replacement = getAnswer(match[1])?.answer?.answerString ?? 'N/A';
						comment = comment.replace(match[0], replacement);
					}
				}

				return (
					<FlexRowSpacedRightAligned key={`line-item-${i}`}>
						<Typography sx={labelStyles} textAlign={'right'}>
							{comment}:
						</Typography>
						<Typography
							title={`${formatted}`}
							sx={labelStyles}
							overflow={'hidden'}
							textOverflow={'ellipsis'}
							textAlign={'right'}
							width={'9ch'}
						>
							{formatted}
						</Typography>
					</FlexRowSpacedRightAligned>
				);
			})}

			{totalTaxAmount !== 0 ? (
				!taxRates ? (
					<Skeleton />
				) : (
					<FlexRowSpacedRightAligned>
						<Typography sx={labelStyles} textAlign={'right'}>
							{taxRates?.label ?? 'Tax'}:
						</Typography>
						<Typography sx={labelStyles} overflow={'hidden'} textOverflow={'ellipsis'} textAlign={'right'} width={'9ch'}>
							{formatCurrencyFromMinorUnits(totalTaxAmount, defaultCurrency)}
						</Typography>
					</FlexRowSpacedRightAligned>
				)
			) : null}

			<Divider style={{ width: '50%', marginLeft: 'auto' }} orientation="horizontal" />
			<Typography sx={labelStyles} textAlign={'right'}>
				Total {summaryLabel}: {formatCurrencyFromMinorUnits(total, defaultCurrency)}
			</Typography>
		</FlexColumnSmallSpaced>
	);
};

export default PaymentSectionSummary;
